<template>
  <div class="min-h-screen">
    <AppHeader />
    
    <!-- Loading State -->
    <div v-if="loading" class="section-padding">
      <div class="container-custom">
        <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
        <SkeletonLoader type="text" width="600px" class="mx-auto mb-8" />
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <SkeletonLoader v-for="i in 3" :key="i" type="card" height="400px" />
        </div>
      </div>
    </div>

    <!-- Service Content -->
    <div v-else-if="service">
      <!-- Hero Section with Banner -->
      <!-- Hero Section with Banner -->
      <!-- Hero Section with Immersive Background -->
      <section class="relative overflow-hidden section-padding min-h-[60vh] flex items-center">
        <!-- Background Image & Overlay -->
        <div class="absolute inset-0 z-0">
          <img 
            v-if="service.image" 
            :src="getBannerUrl(service.image)" 
            :alt="service.name"
            class="w-full h-full object-cover"
          />
          <div v-else class="w-full h-full bg-gray-900"></div>
          <!-- Gradient Overlay -->
          <div class="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        </div>
        
        <div class="container-custom relative z-10">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <!-- Left Column: Content -->
            <div class="space-y-8">
              <!-- Category Badge -->
              <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium shadow-lg">
                <svg class="w-4 h-4 mr-2 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
                {{ service.category?.name || 'Service' }}
              </div>

              <!-- Title -->
              <div class="space-y-6">
                <h1 class="font-heading text-4xl lg:text-6xl font-bold leading-tight text-white">
                  {{ service.name }}
                </h1>
              </div>

              <!-- Short Description -->
              <p class="text-xl text-gray-300 leading-relaxed max-w-xl">
                {{ service.short_description }}
              </p>

              <!-- Key Info Pills (Glassmorphism) -->
              <div class="flex flex-wrap gap-4 mb-8">
                <div class="px-6 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg hover:bg-white/20 transition-all">
                  <div class="text-sm text-gray-300 mb-1">Starting from</div>
                  <div class="text-2xl font-bold text-white">${{ service.starting_price }}</div>
                </div>
                <div class="px-6 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg hover:bg-white/20 transition-all">
                  <div class="text-sm text-gray-300 mb-1">Timeline</div>
                  <div class="text-2xl font-bold text-white">{{ service.timeline_min_weeks }}-{{ service.timeline_max_weeks }} weeks</div>
                </div>
              </div>

              <!-- Hero CTA -->
              <div class="flex flex-wrap gap-4">
                <button 
                  @click="orderViaWhatsApp(service)"
                  class="btn bg-green-500 hover:bg-green-600 text-white border-0 px-8 py-4 text-lg shadow-xl shadow-green-500/10"
                >
                  <span class="flex items-center gap-2">
                    <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp Order
                  </span>
                </button>
                <router-link 
                  :to="{ name: 'ServiceOrder', query: { service: service.uid } }"
                  class="btn bg-white text-gray-900 border-0 hover:bg-gray-100 px-8 py-4 text-lg shadow-xl shadow-white/10"
                >
                  <span class="flex items-center gap-2">
                    <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                    Order Now
                  </span>
                </router-link>
              </div>
            </div>

            <!-- Right Column: Glass Card / Visual -->
            <div class="relative hidden lg:block">
              <div class="relative rounded-3xl overflow-hidden bg-white/5 backdrop-blur-xl border border-white/10 p-8 shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-500">
                <!-- Decorative Icon -->
                <div class="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-white mb-4">Why Choose This Service?</h3>
                <ul class="space-y-4">
                  <li class="flex items-start text-gray-300">
                    <svg class="w-5 h-5 text-primary-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Professional Quality
                  </li>
                  <li class="flex items-start text-gray-300">
                    <svg class="w-5 h-5 text-primary-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Dedicated Support
                  </li>
                  <li class="flex items-start text-gray-300">
                    <svg class="w-5 h-5 text-primary-400 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Timely Delivery
                  </li>
                </ul>
              </div>
              
              <!-- Decorative Glow -->
              <div class="absolute -top-10 -right-10 w-40 h-40 bg-primary-500/30 rounded-full blur-3xl -z-10"></div>
              <div class="absolute -bottom-10 -left-10 w-40 h-40 bg-secondary-500/30 rounded-full blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Service Overview Section -->
      <section class="section-padding bg-white">
        <div class="container-custom">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Long Description -->
            <div>
              <h2 class="font-heading text-3xl font-bold text-gray-900 mb-6">
                Overview
              </h2>
              <div class="prose prose-lg max-w-none text-gray-600 leading-relaxed">
                {{ service.long_description }}
              </div>
            </div>

            <!-- Features & Technologies -->
            <div class="space-y-8">
              <!-- Key Features -->
              <div v-if="service.features && service.features.length > 0">
                <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">
                  Key Features
                </h3>
                <ul class="space-y-3">
                  <li 
                    v-for="(feature, index) in service.features" 
                    :key="index"
                    class="flex items-start group"
                  >
                    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5 group-hover:bg-primary-200 transition-colors">
                      <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span class="ml-3 text-gray-700">{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <!-- Technologies -->
              <div v-if="service.technologies && service.technologies.length > 0">
                <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">
                  Technologies
                </h3>
                <div class="flex flex-wrap gap-2">
                  <span 
                    v-for="(tech, index) in service.technologies" 
                    :key="index"
                    class="px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 text-primary-700 rounded-full text-sm font-medium border border-primary-200 hover:shadow-md transition-shadow"
                  >
                    {{ tech }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Pricing Packages Section -->
      <section v-if="packages.length > 0" class="section-padding bg-gradient-to-br from-gray-50 to-primary-50/30">
        <div class="container-custom">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Pricing Packages
            </div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Package
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              Select the package that best fits your needs and budget
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div 
              v-for="pkg in packages" 
              :key="pkg.id"
              class="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-primary-300"
              :class="{ 'lg:scale-105 border-primary-500 shadow-xl': pkg.recommended }"
            >
              <!-- Recommended Badge -->
              <div v-if="pkg.recommended" class="absolute top-0 right-0 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-1 text-sm font-bold rounded-bl-2xl shadow-lg">
                RECOMMENDED
              </div>

              <div class="p-8">
                <!-- Package Name -->
                <h3 class="font-heading text-2xl font-bold text-gray-900 mb-2">
                  {{ pkg.name }}
                </h3>
                
                <!-- Package Description -->
                <p class="text-gray-600 mb-6 h-12">
                  {{ pkg.description }}
                </p>

                <!-- Price -->
                <div class="mb-6">
                  <div class="flex items-baseline">
                    <span class="text-4xl font-bold text-gray-900">${{ pkg.price }}</span>
                    <span v-if="pkg.billing_cycle" class="ml-2 text-gray-500">/ {{ pkg.billing_cycle }}</span>
                  </div>
                </div>

                <!-- Features -->
                <ul class="space-y-3 mb-8">
                  <li 
                    v-for="(feature, index) in pkg.features" 
                    :key="index"
                    class="flex items-start text-sm"
                  >
                    <svg class="w-5 h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span class="text-gray-700">{{ feature }}</span>
                  </li>
                </ul>

                <div class="grid grid-cols-1 gap-3">
                  <!-- WhatsApp Order -->
                  <button 
                    @click="orderViaWhatsApp(service, pkg)"
                    class="flex items-center justify-center gap-2 w-full px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </button>

                  <!-- Regular Order -->
                  <router-link 
                    :to="{ name: 'ServiceOrder', query: { service: service.uid, package: pkg.name } }"
                    class="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                    :class="pkg.recommended 
                      ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white shadow-lg hover:shadow-xl hover:scale-105' 
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                    Order Now
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Related Portfolios Section -->
      <section v-if="relatedPortfolios.length > 0" class="section-padding bg-white">
        <div class="container-custom">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
              Our Work
            </div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Projects Built with This Service
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              See real examples of what we've accomplished
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <router-link
              v-for="portfolio in relatedPortfolios"
              :key="portfolio.id"
              :to="{ name: 'PortfolioDetail', params: { id: portfolio.uid } }"
              class="group card overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <!-- Portfolio Image -->
              <div class="relative h-48 bg-gray-200 overflow-hidden">
                <img 
                  v-if="portfolio.featured_image"
                  :src="getPortfolioImageUrl(portfolio.featured_image)" 
                  :alt="portfolio.title"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <!-- Portfolio Info -->
              <div class="p-6">
                <div class="flex items-center gap-2 mb-2">
                  <span class="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded">
                    {{ portfolio.category }}
                  </span>
                </div>
                <h3 class="font-heading text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {{ portfolio.title }}
                </h3>
                <p class="text-gray-600 text-sm line-clamp-2">
                  {{ portfolio.description }}
                </p>
              </div>
            </router-link>
          </div>
        </div>
      </section>

      <!-- Other Services Section -->
      <section v-if="otherServices.length > 0" class="section-padding bg-gradient-to-br from-gray-50 via-primary-50/30 to-secondary-50/30">
        <div class="container-custom">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
              </svg>
              Explore More
            </div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Other Services We Offer
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover more ways we can help your business grow
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <router-link
              v-for="otherService in otherServices"
              :key="otherService.id"
              :to="{ name: 'ServiceDetail', params: { id: otherService.uid } }"
              class="group card overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <!-- Service Banner -->
              <div class="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
                <img 
                  v-if="otherService.image"
                  :src="getBannerUrl(otherService.image)" 
                  :alt="otherService.name"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                

              </div>

              <!-- Service Info -->
              <div class="p-6">
                <h3 class="font-heading text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {{ otherService.name }}
                </h3>
                <p class="text-gray-600 mb-4 line-clamp-2">
                  {{ otherService.short_description }}
                </p>
                
                <div class="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p class="text-xs text-gray-500 mb-1">Starting from</p>
                    <p class="text-lg font-bold text-primary-600">${{ otherService.starting_price }}</p>
                  </div>
                  <div class="text-right">
                    <p class="text-xs text-gray-500 mb-1">Timeline</p>
                    <p class="text-sm font-semibold text-gray-700">{{ otherService.timeline_min_weeks }}-{{ otherService.timeline_max_weeks }} weeks</p>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="relative overflow-hidden bg-gray-50 section-padding">
        <!-- Background Elements -->
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
          <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        </div>

        <div class="container-custom text-center relative z-10">
          <div class="max-w-4xl mx-auto">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
              Let's Work Together
            </div>
            <h2 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ready to Get Started?
            </h2>
            <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Contact us today to discuss your project and see how {{ service.name }} can help transform your business.
            </p>
            <div class="flex flex-wrap gap-4 justify-center">
              <button 
                @click="orderViaWhatsApp(service)"
                class="btn bg-green-500 hover:bg-green-600 text-white text-lg px-8 py-4 shadow-xl shadow-green-500/20 hover:shadow-green-500/40 flex items-center gap-2"
              >
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Contact via WhatsApp
              </button>
              <router-link 
                to="/services" 
                class="btn bg-white border-2 border-gray-300 text-gray-900 hover:border-gray-400 text-lg px-8 py-4"
              >
                View All Services
              </router-link>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Error State -->
    <div v-else class="section-padding">
      <div class="container-custom text-center">
        <div class="max-w-md mx-auto">
          <svg class="w-24 h-24 mx-auto text-gray-400 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h2 class="font-heading text-3xl font-bold text-gray-900 mb-4">Service Not Found</h2>
          <p class="text-gray-600 mb-8">The service you're looking for doesn't exist or has been removed.</p>
          <router-link to="/services" class="btn btn-primary">
            View All Services
          </router-link>
        </div>
      </div>
    </div>
    
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useServiceStore } from '@/stores/services'
import { usePortfolioStore } from '@/stores/portfolio'
import { useSettingsStore } from '@/stores/settings'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const route = useRoute()
const router = useRouter()
const serviceStore = useServiceStore()
const portfolioStore = usePortfolioStore()
const settingsStore = useSettingsStore()

const loading = ref(true)
const service = ref(null)
const packages = ref([])

onMounted(async () => {
  // Fetch settings for WhatsApp number
  if (!settingsStore.settings || Object.keys(settingsStore.settings).length === 0) {
    await settingsStore.fetchSettings()
  }
  await loadServiceDetails()
})

// Watch for route changes (when user clicks on another service)
watch(() => route.params.id, async (newId) => {
  if (newId) {
    await loadServiceDetails()
  }
})

const loadServiceDetails = async () => {
  loading.value = true
  try {
    const id = route.params.id
    
    // Fetch service details
    const serviceData = await serviceStore.fetchServiceById(id)
    
    if (serviceData) {
      service.value = serviceData
      packages.value = serviceData.packages || []
      
      // Fetch portfolios if needed
      if (!portfolioStore.portfolioItems || portfolioStore.portfolioItems.length === 0) {
        await portfolioStore.fetchPortfolio()
      }
    } else {
      service.value = null
    }
  } catch (error) {
    console.error('Error loading service details:', error)
    service.value = null
  } finally {
    loading.value = false
  }
}

// WhatsApp order function
const orderViaWhatsApp = (serviceData, packageData = null) => {
  const whatsappNumber = settingsStore.settings?.whatsapp_number || '+8801974611004'
  
  let message = `Hi! I'm interested in ordering:\n\n`
  message += `📋 *Service:* ${serviceData.name}\n`
  
  if (packageData) {
    message += `📦 *Package:* ${packageData.name}\n`
    message += `💰 *Price:* $${packageData.price}\n`
    if (packageData.features && packageData.features.length > 0) {
      message += `\n*Features included:*\n`
      packageData.features.slice(0, 5).forEach(feature => {
        message += `  ✓ ${feature}\n`
      })
    }
  } else {
    message += `💰 *Starting Price:* $${serviceData.starting_price}\n`
  }
  
  message += `\nPlease provide more details about the process and next steps.`
  
  const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '')
  const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
  window.open(whatsappUrl, '_blank')
}

// Computed: Related portfolios that use this service
const relatedPortfolios = computed(() => {
  if (!service.value) return []
  
  return portfolioStore.portfolioItems.filter(portfolio => {
    // Check if portfolio has services array and includes current service
    return portfolio.services && portfolio.services.some(s => s.id === service.value.id)
  }).slice(0, 6) // Limit to 6 portfolios
})

// Computed: Other services (excluding current)
const otherServices = computed(() => {
  if (!service.value) return []
  
  return serviceStore.getActiveServices()
    .filter(s => s.id !== service.value.id)
    .slice(0, 6) // Limit to 6 services
})



const getBannerUrl = (imagePath) => {
  return serviceStore.getBannerUrl(imagePath)
}

const getPortfolioImageUrl = (imagePath) => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
  const baseUrl = API_BASE_URL.replace('/api/v1', '')
  return `${baseUrl}/storage/${imagePath}`
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.prose {
  max-width: none;
}
</style>
